import React, { useState, useEffect } from 'react';

import EmailBuilder from '../EmailBuilder';
import { stripEmailBorders } from '../../utils/stripEmailBorders';
import { applyPreviewFonts, previewFontCss } from '../../utils/emailFonts';
import EmailNotificationConditions from './EmailNotificationConditions';
import {
  X,
  Eye,
  Sparkles
} from 'lucide-react';

const defaultEmailTypeState = {
  is_enabled: true,
  recipient_emails: '',
  from_name: '',
  from_email_address: '',
  apikey_id: null,
  email_subject: '',
  email_body: ''
};

const mergeEmailTypesFromSettings = (src) => ({
  submission: { ...defaultEmailTypeState, ...(src.submission || {}) },
  instant_win: { ...defaultEmailTypeState, ...(src.instant_win || {}) },
  rejected: { ...defaultEmailTypeState, ...(src.rejected || {}) },
  gift_card: { ...defaultEmailTypeState, ...(src.gift_card || {}) },
  membership: { ...defaultEmailTypeState, ...(src.membership || {}) }
});

const EmailSettings = ({
  campaignId,
  settings = {
    submission: { ...defaultEmailTypeState },
    instant_win: { ...defaultEmailTypeState },
    rejected: { ...defaultEmailTypeState }
  },
  onUpdate,
  api,
  campaignName = '',
  setSuccessMessage,
  setShowSuccess,
  theme = {},
  activeForm = null,
  formFields = []
}) => {
  const [emailSettings, setEmailSettings] = useState(() => mergeEmailTypesFromSettings(settings));
  const [sendgridKeys, setSendgridKeys] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('submission');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [showAIHelper, setShowAIHelper] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showInlinePreview, setShowInlinePreview] = useState(false);

  useEffect(() => {
    setEmailSettings(mergeEmailTypesFromSettings(settings));
  }, [settings]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/email-sendgrid-keys');
        if (!cancelled) {
          setSendgridKeys(res.data.keys || []);
        }
      } catch (e) {
        console.error('Failed to load SendGrid keys', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, campaignId]);

  // Available variables for drag and drop
  const availableVariables = {
    instant_win: [
      { variable: '{{campaign_logo}}', description: 'Campaign logo URL (use in img src)' },
      { variable: '{{campaign_name}}', description: 'Campaign name' },
      { variable: '{{prize_name}}', description: 'Prize name' },
      { variable: '{{prize_value}}', description: 'Prize value' },
      { variable: '{{voucher_code}}', description: 'Voucher code' },
      { variable: '{{voucher_details}}', description: 'Full voucher block (code, expiry, etc.)' },
      { variable: '{{expiry_date}}', description: 'Voucher expiry date' },
      { variable: '{{first_name}}', description: 'First name from entry data' },
      { variable: '{{user_name}}', description: 'User name' },
      { variable: '{{primary_color}}', description: 'Primary theme color' },
      { variable: '{{secondary_color}}', description: 'Secondary theme color' },
      { variable: '{{accent_color}}', description: 'Accent theme color' }
    ],
    submission: [
      { variable: '{{campaign_logo}}', description: 'Campaign logo URL (use in img src)' },
      { variable: '{{campaign_name}}', description: 'Campaign name' },
      { variable: '{{entry_number}}', description: 'Entry number' },
      { variable: '{{submission_date}}', description: 'Submission date' },
      { variable: '{{user_name}}', description: 'User name' },
      { variable: '{{first_name}}', description: 'First name from entry data' },
      { variable: '{{user_email}}', description: 'User email' },
      { variable: '{{form_data}}', description: 'Full form submission details' },
      { variable: '{{logo_url}}', description: 'Logo URL from theme' },
      { variable: '{{primary_color}}', description: 'Primary theme color' },
      { variable: '{{secondary_color}}', description: 'Secondary theme color' },
      { variable: '{{accent_color}}', description: 'Accent theme color' }
    ],
    rejected: [
      { variable: '{{campaign_logo}}', description: 'Campaign logo URL (use in img src)' },
      { variable: '{{campaign_name}}', description: 'Campaign name' },
      { variable: '{{entry_number}}', description: 'Entry number' },
      { variable: '{{submission_date}}', description: 'Submission date' },
      { variable: '{{user_name}}', description: 'User name' },
      { variable: '{{first_name}}', description: 'First name from entry data' },
      { variable: '{{user_email}}', description: 'User email' },
      { variable: '{{rejection_reason}}', description: 'Reason for rejection' }
    ],
    gift_card: [
      { variable: '{{campaign_name}}', description: 'Campaign name' },
      { variable: '{{gift_card_amount}}', description: 'Gift card amount (e.g., $50.00)' },
      { variable: '{{gift_card_code}}', description: 'Gift card code' },
      { variable: '{{gift_card_number}}', description: 'Gift card number' },
      { variable: '{{order_id}}', description: 'Order ID' },
      { variable: '{{redemption_link}}', description: 'Redemption link URL' },
      { variable: '{{redemption_link_button}}', description: 'Redemption link button HTML' },
      { variable: '{{expiry_date}}', description: 'Expiry date' },
      { variable: '{{user_name}}', description: 'User name' },
      { variable: '{{first_name}}', description: 'First name from entry data' },
      { variable: '{{primary_color}}', description: 'Primary color from theme' },
      { variable: '{{secondary_color}}', description: 'Secondary color from theme' },
      { variable: '{{accent_color}}', description: 'Accent color from theme' },
      { variable: '{{logo_url}}', description: 'Logo URL from theme' },
      { variable: '{{banner_image_url}}', description: 'Banner image URL from theme' },
      { variable: '{{footer_image_url}}', description: 'Footer image URL' },
      { variable: '{{footer_image_link}}', description: 'Footer image link URL' },
      { variable: '{{facebook_url}}', description: 'Facebook URL' },
      { variable: '{{facebook_icon}}', description: 'Facebook icon URL' },
      { variable: '{{instagram_url}}', description: 'Instagram URL' },
      { variable: '{{instagram_icon}}', description: 'Instagram icon URL' },
      { variable: '{{copyright_text}}', description: 'Copyright text' }
    ],
    membership: [
      { variable: '{{campaign_name}}', description: 'Campaign name' },
      { variable: '{{member_number}}', description: 'Member number (e.g., MBR-00000001)' },
      { variable: '{{member_name}}', description: 'Member full name' },
      { variable: '{{first_name}}', description: 'Member first name' },
      { variable: '{{user_name}}', description: 'Member full name' },
      { variable: '{{logo_url}}', description: 'Logo URL from theme' },
      { variable: '{{primary_color}}', description: 'Primary color from theme' },
      { variable: '{{secondary_color}}', description: 'Secondary color from theme' },
      { variable: '{{accent_color}}', description: 'Accent color from theme' }
    ]
  };

  // AI Helper function
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return;
    
    setAiLoading(true);
    try {
      // Call the AI service using the authenticated api object
      const response = await api.post('/admin/ai/generate-email', {
        prompt: aiPrompt,
        emailType: activeTab,
        variables: availableVariables[activeTab],
        currentEmailBody: emailSettings[activeTab]?.email_body || ''
      });
      
      if (response.data && response.data.html) {
        handleFieldChange(activeTab, 'email_body', response.data.html);
        setAiPrompt('');
        setShowAIHelper(false);
      } else {
        console.error('AI generation failed: No HTML content received');
        alert('AI generation failed: No content received');
      }
    } catch (error) {
      console.error('AI generation failed:', error);
      alert('AI generation failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleFieldChange = (type, field, value) => {
    // Extract the actual value if it's an object with target.value structure
    let actualValue = value;
    if (value && typeof value === 'object' && value.target && value.target.value !== undefined) {
      actualValue = value.target.value;
    }
    
    setEmailSettings(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: actualValue
      }
    }));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      // Save submission email settings
      await api.put(`/admin/campaigns/${campaignId}/email-settings`, {
        emailType: 'submission',
        settings: emailSettings.submission
      });
      
      // Save instant win email settings
      await api.put(`/admin/campaigns/${campaignId}/email-settings`, {
        emailType: 'instant_win',
        settings: emailSettings.instant_win
      });
      
      // Save rejected email settings
      await api.put(`/admin/campaigns/${campaignId}/email-settings`, {
        emailType: 'rejected',
        settings: emailSettings.rejected
      });
      
      // Save gift card email settings
      await api.put(`/admin/campaigns/${campaignId}/email-settings`, {
        emailType: 'gift_card',
        settings: emailSettings.gift_card
      });

      // Save membership email settings
      await api.put(`/admin/campaigns/${campaignId}/email-settings`, {
        emailType: 'membership',
        settings: emailSettings.membership
      });
      
      onUpdate(emailSettings);
      setSuccessMessage('Email settings saved successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to save email settings:', err);
      alert('Failed to save email settings: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePreview = (type) => {
    // Always use the actual email_body from settings and ensure it's a string
    let emailBody = emailSettings[type]?.email_body || '';
    
    // Ensure emailBody is a string
    if (typeof emailBody !== 'string') {
      emailBody = String(emailBody);
    }
    
    // Replace variables with sample data for preview
    const sampleData = {
      campaign_name: campaignName || 'Sample Campaign',
      first_name: 'John',
      user_name: 'John Doe',
      user_email: 'john@example.com',
      entry_number: '12345',
      submission_date: new Date().toLocaleString(),
      prize_name: 'Sample Prize',
      prize_value: '100',
      voucher_code: 'ABC123',
      rejection_reason: 'Invalid receipt or product not found',
      member_number: 'MBR-00000001',
      member_name: 'John Doe',
      gift_card_amount: '$50.00',
      gift_card_code: 'GC-ABC123XYZ',
      gift_card_number: '1234567890',
      order_id: 'ORD-12345',
      redemption_link: 'https://example.com/redeem',
      redemption_link_button: '<p style="margin:20px 0; text-align:center;"><a href="https://example.com/redeem" style="display:inline-block; background:#FF5722; color:white; padding:12px 24px; text-decoration:none; border-radius:5px; font-weight:bold;">Redeem Your Gift Card</a></p>',
      expiry_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      primary_color: theme.primary_color || '#1a5490',
      secondary_color: theme.secondary_color || '#4CAF50',
      accent_color: theme.accent_color || '#FF5722',
      logo_url: theme.logo_url || 'https://via.placeholder.com/200x80/14B8A6/ffffff?text=Campaign+Logo',
      banner_image_url: theme.background_image_url || 'https://via.placeholder.com/600x200/4CAF50/ffffff?text=Banner+Image',
      footer_image_url: 'https://via.placeholder.com/600x200/FF5722/ffffff?text=Footer+Image',
      footer_image_link: 'https://example.com',
      facebook_url: 'https://www.facebook.com/example',
      facebook_icon: 'https://via.placeholder.com/24/1877F2/ffffff?text=F',
      instagram_url: 'https://www.instagram.com/example',
      instagram_icon: 'https://via.placeholder.com/24/E4405F/ffffff?text=I',
      copyright_text: `© ${new Date().getFullYear()} ${campaignName || 'Sample Campaign'}. All rights reserved.`
    };

    // Replace all variables except campaign_logo
    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      emailBody = emailBody.replace(regex, sampleData[key]);
    });

    // Special handling for campaign_logo to prevent double encoding
    if (theme.logo_url) {
      emailBody = emailBody.replace(/\{\{campaign_logo\}\}/g, theme.logo_url);
    } else {
      emailBody = emailBody.replace(/\{\{campaign_logo\}\}/g, 'https://via.placeholder.com/200x80/14B8A6/ffffff?text=Campaign+Logo');
    }
    
    // Replace logo_url if not already replaced
    if (!emailBody.includes(theme.logo_url || '')) {
      emailBody = emailBody.replace(/\{\{logo_url\}\}/g, theme.logo_url || 'https://via.placeholder.com/200x80/14B8A6/ffffff?text=Campaign+Logo');
    }

    // Add CSS styles for proper email rendering
    const emailWithStyles = `
      <style>
        ul { list-style-type: disc; margin-left: 20px; padding-left: 0; }
        li { margin-bottom: 8px; }
        ol { list-style-type: decimal; margin-left: 20px; padding-left: 0; }
        p { margin: 10px 0; }
        h1, h2, h3, h4, h5, h6 { margin: 15px 0 10px 0; }
        img { max-width: 100%; height: auto; }
        table { border-collapse: collapse; width: 100%; }
        td, th { padding: 8px; }
        a { color: #007bff; text-decoration: underline; }
        .email-preview-root { font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #333; }
        .email-preview-root * { font-family: inherit; }
        ${previewFontCss(theme?.font_settings)}
        .email-preview-root b, .email-preview-root strong, .email-preview-root h1, .email-preview-root h2, .email-preview-root h3 { font-weight: 700; }
      </style>
      <div class="email-preview-root">${applyPreviewFonts(stripEmailBorders(emailBody), theme?.font_settings)}</div>
    `;

    setPreviewContent(emailWithStyles);
    setShowInlinePreview(true);
  };

  return (
    <div className="space-y-6">
      {/* AI Helper Modal */}
      {showAIHelper && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowAIHelper(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Sparkles className="h-5 w-5 mr-2 text-purple-500" />
                AI Email Generator
              </h3>
              <button
                onClick={() => setShowAIHelper(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe your email:
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows={4}
                  placeholder="e.g., Create a congratulatory email for instant win with voucher details and instructions..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowAIHelper(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAIGenerate}
                  disabled={aiLoading || !aiPrompt.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {aiLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowPreviewModal(false)}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Email Preview</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('submission')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'submission'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Submission Email
          </button>
          <button
            onClick={() => setActiveTab('instant_win')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'instant_win'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Instant Win Email
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rejected'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Rejected Email
          </button>
          <button
            onClick={() => setActiveTab('gift_card')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'gift_card'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Gift Card Email
          </button>
          <button
            onClick={() => setActiveTab('membership')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'membership'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Membership Email
          </button>
          <button
            onClick={() => setActiveTab('conditions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'conditions'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Email Conditions
          </button>
        </nav>
      </div>

      {/* Email Settings Form */}
      {activeTab !== 'conditions' && (
        <div className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {activeTab === 'submission' ? 'Submission Email' : 
               activeTab === 'instant_win' ? 'Instant Win Email' : 
               activeTab === 'rejected' ? 'Rejected Email' :
               activeTab === 'gift_card' ? 'Gift Card Email' :
               activeTab === 'membership' ? 'Membership Email' : 'Email'}
            </h3>
            <p className="text-sm text-gray-500">
              {activeTab === 'submission' 
                ? 'Email sent when someone submits an entry'
                : activeTab === 'instant_win'
                ? 'Email sent when someone wins instantly'
                : activeTab === 'rejected'
                ? 'Email sent when an entry is rejected'
                : activeTab === 'gift_card'
                ? 'Email sent when a gift card is assigned'
                : activeTab === 'membership'
                ? 'Email sent with Apple Wallet membership card when a membership campaign is submitted'
                : 'Email notification'
              }
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={emailSettings[activeTab]?.is_enabled || false}
              onChange={(e) => handleFieldChange(activeTab, 'is_enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-teal-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">Enable</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SendGrid API key
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white"
            value={emailSettings[activeTab]?.apikey_id || ''}
            onChange={(e) => {
              const v = e.target.value;
              handleFieldChange(activeTab, 'apikey_id', v === '' ? null : v);
            }}
          >
            <option value="">Default (server SENDGRID_API_KEY)</option>
            {sendgridKeys.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Optional. Chooses which stored SendGrid key to use for this email type; otherwise the server default key is used.
          </p>
        </div>

        {/* Basic Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Name
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              value={emailSettings[activeTab]?.from_name || ''}
              onChange={(e) => handleFieldChange(activeTab, 'from_name', e.target.value)}
              placeholder="Your Company Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Email Address
            </label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              value={emailSettings[activeTab]?.from_email_address || ''}
              onChange={(e) => handleFieldChange(activeTab, 'from_email_address', e.target.value)}
              placeholder="noreply@yourcompany.com"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Subject
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            value={emailSettings[activeTab]?.email_subject || ''}
            onChange={(e) => handleFieldChange(activeTab, 'email_subject', e.target.value)}
            placeholder={`${activeTab === 'submission' ? 'Thank you for your entry' :
                         activeTab === 'instant_win' ? 'Congratulations! You won' :
                         activeTab === 'rejected' ? 'Entry Update - Please Review' :
                         activeTab === 'gift_card' ? 'Your Gift Card from {{campaign_name}}' : 'Email Subject'}`}
          />
        </div>

        {/* HTML Editor */}
        <div>
          {!showInlinePreview && (
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Email Content
              </label>
              <button
                onClick={() => setShowAIHelper(true)}
                className="flex items-center px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                AI Help
              </button>
            </div>
          )}
          
          {!showInlinePreview ? (
            <>
              <EmailBuilder
                value={emailSettings[activeTab]?.email_body || ''}
                onChange={(html) => handleFieldChange(activeTab, 'email_body', html)}
                variables={availableVariables[activeTab]}
              />
            </>
          ) : (
            /* Email Preview */
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700">Email Preview</h4>
              </div>
              <div className="p-4 bg-white max-h-96 overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: previewContent }} />
              </div>
            </div>
          )}
        </div>

        {/* Save Button and Preview */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => showInlinePreview ? setShowInlinePreview(false) : handlePreview(activeTab)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye className="h-4 w-4 mr-2" />
            {showInlinePreview ? 'Hide Preview' : 'Preview Email'}
          </button>
          
          <button
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {savingSettings ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Email Settings'
            )}
          </button>
        </div>
        </div>
      )}

      {/* Email Notification Conditions Tab */}
      {activeTab === 'conditions' && (
        <EmailNotificationConditions
          campaignId={campaignId}
          activeForm={activeForm}
          formFields={formFields}
          api={api}
          setSuccessMessage={setSuccessMessage}
          setShowSuccess={setShowSuccess}
          campaign={theme}
        />
      )}
    </div>
  );
};

export default EmailSettings;